import { zodResolver } from '@hookform/resolvers/zod'
import { Turnstile } from '@marsidev/react-turnstile'
import * as Tabs from '@radix-ui/react-tabs'
import { ArrowLeft, AtSign, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { Brand } from '../components/Brand'
import { Button, FormField } from '../components/ui'
import { useAuth } from '../context/AuthContext'

const loginSchema = z.object({
  email: z.email('Informe um e-mail válido.'),
  password: z.string().min(8, 'Use pelo menos 8 caracteres.')
})

const signupSchema = loginSchema.extend({
  displayName: z.string().trim().min(2, 'Informe seu nome.').max(60),
  username: z.string().trim().min(3, 'Use pelo menos 3 caracteres.').max(24).regex(/^[a-z0-9_]+$/, 'Use apenas letras minúsculas, números e _.')
})

type LoginForm = z.infer<typeof loginSchema>
type SignupForm = z.infer<typeof signupSchema>

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim()
const turnstileEnabled = Boolean(turnstileSiteKey && !turnstileSiteKey.includes('SUBSTITUA'))
const googleOauthEnabled = import.meta.env.VITE_GOOGLE_OAUTH_ENABLED === 'true'

function GoogleGIcon() {
  return (
    <svg className="google-g" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.715v2.258h2.909c1.702-1.567 2.684-3.878 2.684-6.614Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.468-.806 5.956-2.18l-2.91-2.259c-.806.54-1.835.859-3.046.859-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.963 10.706A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.168.281-1.706V4.962H.956A9 9 0 0 0 0 9c0 1.45.347 2.822.956 4.038l3.007-2.332Z" />
      <path fill="#EA4335" d="M9 3.58c1.322 0 2.508.454 3.442 1.346l2.582-2.582C13.464.892 11.427 0 9 0A9 9 0 0 0 .956 4.962l3.007 2.332C4.672 5.165 6.656 3.58 9 3.58Z" />
    </svg>
  )
}

export default function AuthPage() {
  const { signIn, signUp, signInWithGoogle, isDemo } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [tab, setTab] = useState(location.state?.mode === 'signup' ? 'signup' : 'login')
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState('')
  const [loading, setLoading] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const returnTo = typeof location.state?.returnTo === 'string' ? location.state.returnTo : '/'

  const loginForm = useForm<LoginForm>({ resolver: zodResolver(loginSchema) })
  const signupForm = useForm<SignupForm>({ resolver: zodResolver(signupSchema) })

  const handleLogin = loginForm.handleSubmit(async (values) => {
    if (turnstileEnabled && !captchaToken) return setFormError('Conclua a verificação de segurança.')
    setLoading(true)
    setFormError('')
    const error = await signIn(values.email, values.password, captchaToken)
    setLoading(false)
    if (error) return setFormError(error)
    navigate(returnTo)
  })

  const handleSignup = signupForm.handleSubmit(async (values) => {
    if (turnstileEnabled && !captchaToken) return setFormError('Conclua a verificação de segurança.')
    setLoading(true)
    setFormError('')
    const error = await signUp(values.email, values.password, values.username, values.displayName, captchaToken)
    setLoading(false)
    if (error) return setFormError(error)
    if (isDemo) navigate(returnTo)
    else navigate('/auth/confirmar', { state: { email: values.email } })
  })

  const handleGoogle = async () => {
    setLoading(true)
    const error = await signInWithGoogle()
    setLoading(false)
    if (error) setFormError(error)
    else if (isDemo) navigate(returnTo)
  }

  return (
    <div className="auth-page">
      <div className="auth-brand-panel">
        <div className="auth-brand-panel__glow" />
        <Link to="/" className="auth-back"><ArrowLeft size={18} /> Voltar ao site</Link>
        <div className="auth-brand-message">
          <img src="/brand/logo-512.webp" alt="Logo da LIGA UVM" width="220" height="220" />
          <span className="eyebrow">Entre na disputa</span>
          <h1>Palpite e compita no ranking. <em>100% grátis</em></h1>
          <p>Monte cards, desafie amigos e transforme cada evento em uma competição.</p>
        </div>
      </div>

      <main className="auth-form-panel">
        <div className="auth-mobile-brand">
          <Brand />
          <h1>Palpite e compita no ranking. <em>100% grátis</em></h1>
        </div>
        <div className="auth-card">
          <Tabs.Root value={tab} onValueChange={(value) => { setTab(value); setFormError('') }}>
            <Tabs.List className="auth-tabs">
              <Tabs.Trigger value="login">Entrar</Tabs.Trigger>
              <Tabs.Trigger value="signup">Criar conta</Tabs.Trigger>
            </Tabs.List>

            {googleOauthEnabled ? <><button className="google-button" onClick={handleGoogle} disabled={loading}>
              <GoogleGIcon /> <span>Continuar com Google</span>
            </button><div className="auth-divider"><span>ou use seu e-mail</span></div></> : null}

            {turnstileEnabled ? <Turnstile siteKey={turnstileSiteKey!} options={{ theme: 'dark', language: 'pt-BR', size: 'flexible' }} onSuccess={setCaptchaToken} onExpire={() => setCaptchaToken('')} onError={() => setFormError('A verificação de segurança falhou. Tente novamente.')} /> : null}

            {formError ? <div className="auth-error" role="alert">{formError}</div> : null}

            <Tabs.Content value="login">
              <form className="auth-form" onSubmit={handleLogin}>
                <FormField label="E-mail" error={loginForm.formState.errors.email?.message}>
                  <div className="input-with-icon"><Mail size={18} /><input type="email" autoComplete="email" placeholder="voce@email.com" {...loginForm.register('email')} /></div>
                </FormField>
                <FormField label="Senha" error={loginForm.formState.errors.password?.message}>
                  <div className="input-with-icon"><LockKeyhole size={18} /><input type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="Sua senha" {...loginForm.register('password')} /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
                </FormField>
                <Link className="forgot-link" to="/auth/recuperar">Esqueci minha senha</Link>
                <Button type="submit" loading={loading} className="button--full">Entrar na LIGA UVM</Button>
              </form>
            </Tabs.Content>

            <Tabs.Content value="signup">
              <form className="auth-form" onSubmit={handleSignup}>
                <FormField label="Nome" error={signupForm.formState.errors.displayName?.message}>
                  <div className="input-with-icon"><ShieldCheck size={18} /><input autoComplete="name" placeholder="Como você quer aparecer" {...signupForm.register('displayName')} /></div>
                </FormField>
                <FormField label="@username" error={signupForm.formState.errors.username?.message}>
                  <div className="input-with-icon"><AtSign size={18} /><input autoCapitalize="none" autoComplete="username" placeholder="seu_usuario" {...signupForm.register('username')} /></div>
                </FormField>
                <FormField label="E-mail" error={signupForm.formState.errors.email?.message}>
                  <div className="input-with-icon"><Mail size={18} /><input type="email" autoComplete="email" placeholder="voce@email.com" {...signupForm.register('email')} /></div>
                </FormField>
                <FormField label="Senha" error={signupForm.formState.errors.password?.message}>
                  <div className="input-with-icon"><LockKeyhole size={18} /><input type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Mínimo de 8 caracteres" {...signupForm.register('password')} /><button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
                </FormField>
                <p className="auth-terms">Ao criar a conta, você concorda com os <Link to="/termos">Termos</Link> e a <Link to="/privacidade">Política de Privacidade</Link>.</p>
                <Button type="submit" loading={loading} className="button--full">Criar minha conta</Button>
              </form>
            </Tabs.Content>
          </Tabs.Root>
        </div>
      </main>
    </div>
  )
}

export function RecoverPasswordPage() {
  const { requestPasswordReset } = useAuth()
  const [email, setEmail] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (turnstileEnabled && !captchaToken) {
      setMessage('Conclua a verificação de segurança.')
      return
    }
    setLoading(true)
    const error = await requestPasswordReset(email, captchaToken)
    setLoading(false)
    setMessage(error ?? 'Se existir uma conta com esse e-mail, enviaremos as instruções de recuperação.')
  }

  return <AuthStatusCard title="Recuperar senha" description="Enviaremos um link seguro para você criar uma nova senha."><form className="auth-form" onSubmit={submit}><FormField label="E-mail"><div className="input-with-icon"><Mail size={18} /><input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></div></FormField>{turnstileEnabled ? <Turnstile siteKey={turnstileSiteKey!} options={{ theme: 'dark', language: 'pt-BR', size: 'flexible' }} onSuccess={setCaptchaToken} onExpire={() => setCaptchaToken('')} onError={() => setMessage('A verificação de segurança falhou. Tente novamente.')} /> : null}{message ? <div className="auth-info">{message}</div> : null}<Button type="submit" loading={loading} className="button--full">Enviar instruções</Button></form></AuthStatusCard>
}

export function UpdatePasswordPage() {
  const { updatePassword } = useAuth()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const submit = async (event: React.FormEvent) => { event.preventDefault(); setLoading(true); const error = await updatePassword(password); setLoading(false); if (!error) navigate('/'); }
  return <AuthStatusCard title="Definir nova senha" description="Use pelo menos 8 caracteres e evite senhas reutilizadas."><form className="auth-form" onSubmit={submit}><FormField label="Nova senha"><div className="input-with-icon"><LockKeyhole size={18} /><input required minLength={8} type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></div></FormField><Button type="submit" loading={loading} className="button--full">Atualizar senha</Button></form></AuthStatusCard>
}

export function ConfirmEmailPage() {
  const location = useLocation()
  return <AuthStatusCard title="Confira seu e-mail" description={`Enviamos um link de confirmação para ${location.state?.email ?? 'seu endereço'}.`}><div className="confirmation-mark"><Mail size={28} /></div><p className="auth-status-copy">Depois de confirmar, volte para a LIGA UVM e entre normalmente.</p><Link to="/entrar" className="button button--secondary button--full">Voltar para entrar</Link></AuthStatusCard>
}

function AuthStatusCard({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return <div className="auth-status-page"><div className="auth-status-card"><Brand /><span className="eyebrow">Conta LIGA UVM</span><h1>{title}</h1><p>{description}</p>{children}<Link to="/entrar" className="auth-status-back"><ArrowLeft size={16} /> Voltar</Link></div></div>
}
